// ═══════════════════════════════════════════════════════════
// LoginPage — вхід + реєстрація + скидання пароля
// ═══════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../contexts/I18nContext';
import BUSINESS_TYPES from '../config/businessTypes';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function LoginPage() {
  const { login, resetPassword } = useAuth();
  const { t } = useT();

  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') === 'register' ? 'register' : 'login';
  }); // 'login' | 'register' | 'reset'

  // Selected plan from landing page URL (?plan=start|business|pro)
  const [selectedPlan] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get('plan');
    return ['start', 'business', 'pro'].includes(p) ? p : null;
  });
  const [showPaywall, setShowPaywall] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Register state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regName, setRegName] = useState('');
  const [regBusiness, setRegBusiness] = useState('');
  const [regBusinessType, setRegBusinessType] = useState('');
  const [regShowPassword, setRegShowPassword] = useState(false);
  const [regError, setRegError] = useState('');
  const [regBusy, setRegBusy] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  // Reset state
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        setError(t('login.errorInvalidCreds'));
      } else if (err.code === 'auth/too-many-requests') {
        setError(t('login.errorTooMany'));
      } else {
        setError(t('login.errorGeneric') + err.message);
      }
    }
    setBusy(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');

    if (!regBusiness.trim()) { setRegError(t('login.errorEnterBusiness')); return; }
    if (!regBusinessType) { setRegError(t('login.errorSelectBusinessType')); return; }
    if (!regName.trim()) { setRegError(t('login.errorEnterName')); return; }
    if (!regEmail.trim()) { setRegError(t('login.errorEnterEmail')); return; }
    if (regPassword.length < 6) { setRegError(t('login.errorMinPassword')); return; }
    if (regPassword !== regConfirm) { setRegError(t('login.errorPasswordMismatch')); return; }
    if (!agreeTerms) { setRegError(t('login.errorAgreeTerms')); return; }

    setRegBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/register-tenant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: regEmail.trim(),
          password: regPassword,
          displayName: regName.trim(),
          businessName: regBusiness.trim(),
          businessType: regBusinessType,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 409) {
          setRegError(t('login.errorEmailExists'));
        } else {
          setRegError(t('login.errorRegister') + (data.error || ''));
        }
        setRegBusy(false);
        return;
      }

      // Auto-login after successful registration
      setRegSuccess(true);

      // If plan was pre-selected from landing — set hash so App.js shows paywall after login
      if (selectedPlan) {
        window.location.hash = `#/checkout?plan=${selectedPlan}`;
      }

      await signInWithEmailAndPassword(auth, regEmail.trim(), regPassword);
    } catch (err) {
      setRegError(t('login.errorRegister') + err.message);
      setRegBusy(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) { setResetError(t('login.errorEnterEmail')); return; }
    setResetError('');
    setResetBusy(true);
    try {
      await resetPassword(resetEmail.trim());
      setResetSent(true);
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-email') {
        setResetError(t('login.errorInvalidCreds'));
      } else {
        setResetError(t('login.errorGeneric') + err.message);
      }
    }
    setResetBusy(false);
  };

  const openReset = () => {
    setResetEmail(email);
    setResetError('');
    setResetSent(false);
    setTab('reset');
  };

  const PasswordToggle = ({ show, onToggle }) => (
    <button
      type="button"
      className="password-toggle-btn"
      onClick={onToggle}
      tabIndex={-1}
      title={show ? t('login.hidePassword') : t('login.showPassword')}
    >
      {show ? (
        <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
          <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
        </svg>
      ) : (
        <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
        </svg>
      )}
    </button>
  );

  return (
    <div className="login-page">
      <div className="login-card">
        {tab === 'reset' ? (
          <>
            <h1 className="login-title">{t('login.resetTitle')}</h1>
            <p className="login-subtitle">
              {resetSent ? t('login.resetSentHint') : t('login.resetSubtitle')}
            </p>

            {!resetSent ? (
              <form onSubmit={handleReset} className="login-form">
                <div className="login-field">
                  <label>{t('login.email')}</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)}
                    placeholder="email@example.com"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                {resetError && <div className="login-error">{resetError}</div>}
                <button type="submit" className="btn btn-primary login-btn" disabled={resetBusy}>
                  {resetBusy ? t('login.wait') : t('login.resetSend')}
                </button>
              </form>
            ) : (
              <div className="login-reset-success">
                <span style={{ fontSize: 40 }}>📬</span>
              </div>
            )}

            <button type="button" className="login-forgot-link" onClick={() => setTab('login')}>
              ← {t('login.backToLogin')}
            </button>
          </>
        ) : (
          <>
            {/* Tabs */}
            <div className="login-tabs">
              <button
                className={`login-tab ${tab === 'login' ? 'active' : ''}`}
                onClick={() => setTab('login')}
              >
                {t('login.tabLogin')}
              </button>
              <button
                className={`login-tab ${tab === 'register' ? 'active' : ''}`}
                onClick={() => setTab('register')}
              >
                {t('login.tabRegister')}
              </button>
            </div>

            {tab === 'login' ? (
              <>
                <h1 className="login-title">{t('login.title')}</h1>
                <p className="login-subtitle">{t('login.subtitle')}</p>

                <form onSubmit={handleLogin} className="login-form">
                  <div className="login-field">
                    <label>{t('login.email')}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="email@example.com"
                      autoComplete="email"
                    />
                  </div>
                  <div className="login-field">
                    <label>{t('login.password')}</label>
                    <div className="password-input-wrap">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="••••••"
                        autoComplete="current-password"
                      />
                      <PasswordToggle show={showPassword} onToggle={() => setShowPassword(p => !p)} />
                    </div>
                  </div>

                  {error && <div className="login-error">{error}</div>}

                  <button type="submit" className="btn btn-primary login-btn" disabled={busy}>
                    {busy ? t('login.wait') : t('login.submit')}
                  </button>
                </form>

                <button type="button" className="login-forgot-link" onClick={openReset}>
                  {t('login.forgotPassword')}
                </button>
              </>
            ) : (
              <>
                <h1 className="login-title">{t('login.registerTitle')}</h1>
                <p className="login-subtitle">{t('login.registerSubtitle')}</p>

                {regSuccess ? (
                  <div className="login-reset-success">
                    <span style={{ fontSize: 40 }}>🎉</span>
                    <p style={{ marginTop: 12 }}>{t('login.registerSuccess')}</p>
                  </div>
                ) : (
                  <form onSubmit={handleRegister} className="login-form">
                    <div className="login-field">
                      <label>{t('login.businessLabel')}</label>
                      <input
                        type="text"
                        value={regBusiness}
                        onChange={e => setRegBusiness(e.target.value)}
                        placeholder={t('login.businessPlaceholder')}
                        autoComplete="organization"
                        autoFocus
                      />
                    </div>
                    <div className="login-field">
                      <label>{t('login.businessTypeLabel')}</label>
                      <select
                        value={regBusinessType}
                        onChange={e => setRegBusinessType(e.target.value)}
                        className="login-select"
                      >
                        <option value="">{t('login.businessTypePlaceholder')}</option>
                        {Object.entries(BUSINESS_TYPES).map(([key, bt]) => (
                          <option key={key} value={key}>{bt.icon} {bt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="login-field">
                      <label>{t('login.nameLabel')}</label>
                      <input
                        type="text"
                        value={regName}
                        onChange={e => setRegName(e.target.value)}
                        placeholder={t('login.namePlaceholder')}
                        autoComplete="name"
                      />
                    </div>
                    <div className="login-field">
                      <label>{t('login.email')}</label>
                      <input
                        type="email"
                        value={regEmail}
                        onChange={e => setRegEmail(e.target.value)}
                        placeholder="email@example.com"
                        autoComplete="email"
                      />
                    </div>
                    <div className="login-field">
                      <label>{t('login.password')}</label>
                      <div className="password-input-wrap">
                        <input
                          type={regShowPassword ? 'text' : 'password'}
                          value={regPassword}
                          onChange={e => setRegPassword(e.target.value)}
                          placeholder="••••••"
                          autoComplete="new-password"
                        />
                        <PasswordToggle show={regShowPassword} onToggle={() => setRegShowPassword(p => !p)} />
                      </div>
                    </div>
                    <div className="login-field">
                      <label>{t('login.confirmPassword')}</label>
                      <div className="password-input-wrap">
                        <input
                          type={regShowPassword ? 'text' : 'password'}
                          value={regConfirm}
                          onChange={e => setRegConfirm(e.target.value)}
                          placeholder="••••••"
                          autoComplete="new-password"
                        />
                      </div>
                    </div>

                    {regError && <div className="login-error">{regError}</div>}

                    <button type="submit" className="btn btn-primary login-btn" disabled={regBusy}>
                      {regBusy ? t('login.wait') : t('login.registerSubmit')}
                    </button>
                    <label className="login-agree-checkbox">
                      <input
                        type="checkbox"
                        checked={agreeTerms}
                        onChange={e => setAgreeTerms(e.target.checked)}
                      />
                      <span>
                        {t('login.agreeCheckbox')}{' '}
                        <a href="https://systemic-crm.com/uk/terms" target="_blank" rel="noopener noreferrer">{t('login.terms')}</a>
                        {' '}{t('common.and')}{' '}
                        <a href="https://systemic-crm.com/uk/privacy" target="_blank" rel="noopener noreferrer">{t('login.privacy')}</a>
                      </span>
                    </label>
                  </form>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
